import { useEffect, useState } from "react";
import { FiCheck, FiInfo, FiRefreshCw } from "react-icons/fi";
import { refreshCanalOptions } from "../../constants";
// import * as actions from "../../containers/User/Calculator/actions"
// import { toastError } from '../../utils'
// import { update } from 'lodash'

const initialCanalPort = {
  eca: false,
  hra: false,
  jwc: false,
  suez: false,
  panama: false,
  singapore: false,
};

function PortDistanceModal(props) {
  const {
    calculatorData = {},
    setCalculatorData = () => { },
    onFetchPortOperationDistance = () => { },
    onChangePortCanalInQueryState = () => { },
    onClose = () => { },
  } = props;

  const { portOperation = [], clientQuery = {} } = calculatorData || {};

  // const [userCanal, setUserCanal] = useState({})
  const [canalPorts, setCanalPorts] = useState({ ...initialCanalPort });
  const [routeOverride, setRouteOverride] = useState(false);

  useEffect(() => {
    const {
      eca = false,
      hra = false,
      jwc = false,
      suez = false,
      panama = false,
      singapore = false,
    } = clientQuery || {};
    setCanalPorts({
      eca,
      hra,
      jwc,
      suez,
      panama,
      singapore,
    });
  }, [clientQuery?.suez, clientQuery?.panama, clientQuery?.eca, clientQuery?.hra, clientQuery?.jwc, clientQuery?.singapore]);

  const onChangeDistanceNotes = (index, value) => {
    setCalculatorData((prev) => {
      const prevOps = [...(prev?.portOperation || [])];
      if (prevOps[index]) {
        prevOps[index] = { ...prevOps[index], notes: value };
      }
      return {
        ...prev,
        portOperation: prevOps,
      };
    });
  };

  const onChangeUserDefinedDistance = (index, value, type) => {
    const parsedVal =
      value === "" || (typeof value === "string" && (value.endsWith(".") || value.endsWith(".0")))
        ? value
        : isNaN(value)
          ? value
          : Number(value);

    setCalculatorData((prev) => {
      const prevOps = prev?.portOperation || [];
      const updatedPortOperation = prevOps.map((leg, i) => {
        if (i !== index) return leg;

        const oldCargoDetails = leg.cargoDetails || {};
        const prevPortName = prevOps[i - 1]?.port || "";
        const currentPortName = leg?.port || "";
        const combPortName = prevPortName && currentPortName ? `${prevPortName}_${currentPortName}` : oldCargoDetails.portCombination;

        const updatedCargoDetails = {
          ...oldCargoDetails,
          [type]: parsedVal,
          ...(combPortName ? { portCombination: combPortName } : {}),
        };

        return {
          ...leg,
          cargoDetails: updatedCargoDetails,
          distanceNormal: type === "distNormal" ? parsedVal : leg.distanceNormal,
          distanceECA: type === "distEca" ? parsedVal : leg.distanceECA,
        };
      });

      const updatedClientQuery = {
        ...prev?.clientQuery,
        cargoList: updatedPortOperation,
      };

      const prevPort = updatedPortOperation[index - 1]?.port;
      const currPort = updatedPortOperation[index]?.port;

      if (
        prevPort &&
        currPort &&
        updatedClientQuery?.portToPortDistance?.output?.[`${prevPort}_${currPort}`]
      ) {
        updatedClientQuery.portToPortDistance = {
          ...updatedClientQuery.portToPortDistance,
          output: {
            ...updatedClientQuery.portToPortDistance.output,
            [`${prevPort}_${currPort}`]: {
              ...updatedClientQuery.portToPortDistance.output[`${prevPort}_${currPort}`],
              [type]: parsedVal,
            },
          },
        };
      }

      return {
        ...prev,
        portOperation: updatedPortOperation,
        clientQuery: updatedClientQuery,
      };
    });
  };

  const onSetCanalPort = (e) => {
    const { checked, name } = e.target || {};
    if (!name) return;
    const updatedCanalPorts = { ...canalPorts, [name]: checked };
    setCanalPorts(updatedCanalPorts);
    setRouteOverride(true);
    setCalculatorData((prev) => {
      const current = typeof prev === "object" && prev !== null ? prev : calculatorData;
      return {
        ...current,
        clientQuery: {
          ...(current?.clientQuery || {}),
          [name]: checked,
        },
      };
    });
    onChangePortCanalInQueryState(updatedCanalPorts);
  };

  return (
    <div className="w-full text-[11px] text-slate-700 font-medium">
      <section className="border border-slate-200 bg-slate-50/70">
        <div className="flex items-start gap-2 px-2.5 py-1.5">
          <div className="flex h-4 w-4 shrink-0 items-center justify-center bg-sky-100 text-[#313C4C] mt-0.5">
            <FiInfo className="h-3 w-3" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-[11px] font-medium leading-4 text-slate-900">
              How port distances are calculated
            </h3>
            <p className="mb-0.5 mt-0.5 text-[11px] font-medium leading-4 text-slate-600">
              Distances are split into Normal and ECA segments according to the fuel and emission requirements of the route.
            </p>
            <ul className="m-0 grid list-disc gap-x-10 gap-y-0.5 pl-4 text-[11px] font-medium leading-4 text-slate-600 md:grid-cols-[max-content_1fr]">
              <li>
                <span className="font-medium text-slate-800">Normal distance:</span> allows 0.5% emission and uses Normal-HS fuel for propulsion.
              </li>
              <li>
                <span className="font-medium text-slate-800">ECA distance:</span> requires 0.1% emission and uses ECA-LS fuel for propulsion.
              </li>
              <li>Only Normal or ECA propulsion fuel is consumed for a route segment at a time.</li>
              <li>Light fuel consumption follows the vessel configuration in the consumption table.</li>
              <li className="md:col-span-2">Fuel prices are selected from the configured fuel-type sets on the estimation page.</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-medium leading-4 text-slate-700">
          <span className="font-medium text-[#313C4C]">Route notice:</span> Actual plotted routes and observed distances may differ from these estimates. Review and amend the suggested Normal and ECA distances when required.
        </div>
      </section>

      <section className="my-2 flex flex-wrap items-center justify-start gap-x-4 gap-y-1.5 border border-slate-200 bg-white px-2.5 py-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="font-medium text-[11px] text-slate-700">Canal routing</span>
          {refreshCanalOptions.map((item) => (
            <label
              key={item.value}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-slate-700"
            >
              <input
                type="checkbox"
                name={item.value}
                checked={Boolean(canalPorts[item.value])}
                onChange={onSetCanalPort}
                className="h-3.5 w-3.5 cursor-pointer accent-[#313C4C]"
              />
              <span className="font-medium text-[11px]">{item.label}</span>
            </label>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:ml-2">
          <button
            type="button"
            className="inline-flex h-6 items-center gap-1.5 border border-slate-300 bg-white px-2.5 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-200 cursor-pointer"
            onClick={() => {
              onFetchPortOperationDistance(true, null, {
                ...canalPorts,
                routeOverride,
              });
              setRouteOverride(false);
            }}
          >
            <FiRefreshCw className="h-3 w-3" aria-hidden="true" />
            Reset distances
          </button>
          <button
            type="button"
            className="inline-flex h-6 items-center gap-1.5 border border-[#313C4C] bg-[#313C4C] px-2.5 text-[11px] font-medium text-white transition-colors hover:bg-[#0c2f50] focus:outline-none focus:ring-2 focus:ring-sky-200 cursor-pointer"
            onClick={() => {
              onFetchPortOperationDistance(false, null, {
                ...canalPorts,
                routeOverride,
              }, false);
              setRouteOverride(false);
              onClose();
            }}
          >
            <FiCheck className="h-3 w-3" aria-hidden="true" />
            Save distance
          </button>
        </div>
      </section>

      <section className="overflow-hidden border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-2.5 py-1.5">
          <div>
            <h3 className="m-0 text-[11px] font-medium text-slate-900">Port distance details</h3>
            <p className="m-0 mt-0.5 text-[11px] font-medium text-slate-500">System values are reference distances. User values can be amended for this estimate.</p>
          </div>
          <span className="border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500">
            {Array.isArray(portOperation) ? portOperation.length : 0} ports
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1050px] w-full border-collapse text-[11px]">
            <thead className="bg-[#e2e8f0] text-[#313C4C]">
              <tr>
                {[
                  ["No", "w-[34px]"],
                  ["Port", "min-w-[130px]"],
                  ["Normal (System)", "min-w-[96px]"],
                  ["ECA (System)", "min-w-[88px]"],
                  ["Normal (User)", "min-w-[100px]"],
                  ["ECA (User)", "min-w-[92px]"],
                  ["Latitude", "min-w-[80px]"],
                  ["Longitude", "min-w-[88px]"],
                  ["Country", "min-w-[78px]"],
                  ["Time Zone", "min-w-[72px]"],
                  ["User Notes", "min-w-[150px]"],
                ].map(([label, width]) => (
                  <th
                    key={label}
                    scope="col"
                    className={`${width} border-r border-[#cbd5e1] px-1.5 py-1.5 text-left font-medium leading-3 last:border-r-0 text-[11px] text-[#313C4C]`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {Array.isArray(portOperation) && portOperation.length > 0 ? (
                portOperation.map((item, index) => {
                  const {
                    port = "",
                    cargoDetails = {},
                    lat = "",
                    lng = "",
                    country = "",
                    notes = "",
                    operation = "",
                  } = item;
                  const {
                    distEca = 0,
                    distNormal = 0,
                    distEcaSystem = 0,
                    distNormalSystem = 0,
                  } = cargoDetails || {};
                  const isCommence = index === 0 || operation === "Commence";
                  const inputClassName = `block h-[19.5px] w-full border-0 px-1.5 text-[11px] font-medium outline-none transition-colors focus:ring-1 focus:ring-inset focus:ring-[#313C4C] ${isCommence
                    ? "cursor-not-allowed bg-slate-100 text-slate-400"
                    : "bg-[#ffffd6] text-slate-800"
                    }`;

                  return (
                    <tr key={index} className="bg-white transition-colors hover:bg-slate-50/80 text-[11px] h-[19.5px]">
                      <td className="border-r border-slate-200 px-1.5 py-0 text-slate-500 font-medium h-[19.5px]">{index + 1}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 font-medium text-slate-800 h-[19.5px]">{port}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 tabular-nums font-medium h-[19.5px]">{distNormalSystem || "0"}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 tabular-nums font-medium h-[19.5px]">{distEcaSystem || "0"}</td>
                      <td className="border-r border-slate-200 p-0 h-[19.5px]">
                        <input
                          type="text"
                          disabled={isCommence}
                          aria-label={`Normal user distance for ${port || `port ${index + 1}`}`}
                          className={inputClassName}
                          value={isCommence ? "0" : (distNormal !== undefined && distNormal !== null ? distNormal : "0")}
                          onChange={(e) => onChangeUserDefinedDistance(index, e.target.value, "distNormal")}
                        />
                      </td>
                      <td className="border-r border-slate-200 p-0 h-[19.5px]">
                        <input
                          type="text"
                          disabled={isCommence}
                          aria-label={`ECA user distance for ${port || `port ${index + 1}`}`}
                          className={inputClassName}
                          value={isCommence ? "0" : (distEca !== undefined && distEca !== null ? distEca : "0")}
                          onChange={(e) => onChangeUserDefinedDistance(index, e.target.value, "distEca")}
                        />
                      </td>
                      <td className="border-r border-slate-200 px-1.5 py-0 tabular-nums text-slate-600 font-medium h-[19.5px]">{lat || "—"}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 tabular-nums text-slate-600 font-medium h-[19.5px]">{lng || "—"}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 text-slate-600 font-medium h-[19.5px]">{country || "—"}</td>
                      <td className="border-r border-slate-200 px-1.5 py-0 text-slate-600 font-medium h-[19.5px]">—</td>
                      <td className="p-0 h-[19.5px]">
                        <input
                          type="text"
                          value={notes || ""}
                          aria-label={`Notes for ${port || `port ${index + 1}`}`}
                          placeholder="Add a note"
                          className="block h-[19.5px] w-full border-0 bg-[#ffffd6] px-1.5 text-[11px] font-medium text-slate-800 outline-none placeholder:text-slate-500 focus:ring-1 focus:ring-inset focus:ring-[#313C4C]"
                          onChange={(e) => onChangeDistanceNotes(index, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-slate-500 font-medium text-[11px]">
                    No port distance records are available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default PortDistanceModal;
